import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft, BookOpen, Lightbulb, HelpCircle, AlertTriangle,
  Sparkles, CheckCircle2, Clock, Brain, Target, Award,
  Volume2, Loader2, Pause
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AskAITutorButton from '@/components/focus/AskAITutorButton';

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// TTSPlayer — embedded audio narration for 7+ Resources
// =============================================================================
// Shows a compact "Listen" button; on first click it loads the cached MP3 from
// the backend and streams through an <audio> element. Only renders for 7+
// subjects (maths-7plus, english-7plus, kumon-7plus).
const TTS_SUPPORTED_SUBJECTS = ['maths-7plus', 'english-7plus', 'kumon-7plus'];
const TTS_SPEED_OPTIONS = [
  { value: 0.75, label: 'Slow' },
  { value: 1.0, label: 'Normal' },
  { value: 1.25, label: 'Fast' },
];
const TTS_SPEED_STORAGE_KEY = 'edpies-tts-speed';

const TTSPlayer = ({ subject, categoryId, topicId, section, label = 'Listen', autoPlay = true }) => {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(() => {
    try {
      const saved = parseFloat(localStorage.getItem(TTS_SPEED_STORAGE_KEY));
      return [0.75, 1.0, 1.25].includes(saved) ? saved : 1.0;
    } catch (_) {
      return 1.0;
    }
  });
  const audioRef = React.useRef(null);

  if (!TTS_SUPPORTED_SUBJECTS.includes(subject)) return null;

  // Apply playbackRate whenever it changes or a new audio is loaded
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
    try { localStorage.setItem(TTS_SPEED_STORAGE_KEY, String(speed)); } catch (_) {}
  }, [speed, audioUrl]);

  // Auto-play on mount (or when the topic/section changes). Modern browsers
  // block audio without a prior user gesture — if so, we silently fail and
  // the student can tap the Listen button.
  useEffect(() => {
    if (!autoPlay) return;
    const url = `${API}/api/resources/tts?subject=${encodeURIComponent(subject)}&category=${encodeURIComponent(categoryId)}&topic=${encodeURIComponent(topicId)}&section=${encodeURIComponent(section)}`;
    setAudioUrl(url);
    const timer = setTimeout(() => {
      audioRef.current?.play().catch(() => {
        // Autoplay blocked — no toast, user can click Listen to start
      });
    }, 250);
    return () => {
      clearTimeout(timer);
      try { audioRef.current?.pause(); } catch (_) {}
    };
  }, [subject, categoryId, topicId, section, autoPlay]);

  const handlePlay = async () => {
    if (!audioUrl) {
      setLoading(true);
      const url = `${API}/api/resources/tts?subject=${encodeURIComponent(subject)}&category=${encodeURIComponent(categoryId)}&topic=${encodeURIComponent(topicId)}&section=${encodeURIComponent(section)}`;
      setAudioUrl(url);
      setTimeout(() => {
        audioRef.current?.play().catch(() => {
          toast.error('Audio playback failed — try again in a moment.');
          setPlaying(false);
        });
      }, 100);
      setLoading(false);
      return;
    }
    if (playing) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play().catch(() => toast.error('Could not play audio'));
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`tts-${section}`}>
      <Button
        size="sm"
        variant="outline"
        onClick={handlePlay}
        disabled={loading}
        className="border-purple-300 text-purple-700 hover:bg-purple-50"
        data-testid={`tts-play-${section}`}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
        ) : playing ? (
          <Pause className="w-4 h-4 mr-1.5" />
        ) : (
          <Volume2 className="w-4 h-4 mr-1.5" />
        )}
        {playing ? 'Pause' : label}
      </Button>

      {/* Speed selector — slower = better for early learners */}
      <div
        className="inline-flex rounded-md border border-gray-200 overflow-hidden bg-gray-50"
        role="group"
        aria-label="Audio speed"
        data-testid={`tts-speed-${section}`}
      >
        {TTS_SPEED_OPTIONS.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setSpeed(opt.value)}
            className={
              'px-2 py-1 text-xs font-medium transition-colors ' +
              (speed === opt.value
                ? 'bg-purple-600 text-white'
                : 'text-slate-500 hover:bg-gray-100')
            }
            title={`Play at ${opt.value}× speed`}
            data-testid={`tts-speed-${opt.value}-${section}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onCanPlay={() => {
            if (audioRef.current) audioRef.current.playbackRate = speed;
            if (autoPlay && audioRef.current?.paused) {
              audioRef.current.play().catch(() => {});
            }
          }}
          onError={() => {
            setPlaying(false);
            toast.error('Audio failed to load');
          }}
          className="h-8 hidden md:inline-block"
          controls
        />
      )}
    </div>
  );
};

const ALL_SECTIONS = [
  { id: 'concept', title: 'Concept', fullTitle: 'Concept Explained', icon: BookOpen, color: 'blue', time: '5 min read', dataKey: 'concept_explained' },
  { id: 'example', title: 'Example', fullTitle: 'Worked Example', icon: Lightbulb, color: 'amber', time: '3 min', dataKey: 'worked_example' },
  { id: 'practice', title: 'Practice', fullTitle: 'Quick Practice', icon: Target, color: 'green', time: '5 questions', dataKey: 'quick_practice' },
  { id: 'mistakes', title: 'Mistakes', fullTitle: 'Common Mistakes', icon: AlertTriangle, color: 'red', time: '2 min', dataKey: 'common_mistakes' },
  { id: 'challenge', title: '11+ Twist', fullTitle: '11+ Twist', icon: Sparkles, color: 'purple', time: 'Challenge', dataKey: 'eleven_plus_twist' }
];

// Returns the subset of sections that actually have data for this topic.
// Also rewrites the challenge tab's title for 7+ subjects.
const getVisibleSections = (topic, subject) => {
  if (!topic) return [];
  const is7Plus = (subject || '').includes('7plus');
  return ALL_SECTIONS
    .filter(s => topic[s.dataKey])
    .map(s => s.id === 'challenge' && is7Plus
      ? { ...s, title: 'Bonus', fullTitle: '7+ Bonus Challenge' }
      : s
    );
};

const TAB_COLORS = {
  blue: { active: 'bg-blue-600 text-white', inactive: 'text-blue-600 hover:bg-blue-50' },
  amber: { active: 'bg-amber-500 text-white', inactive: 'text-amber-600 hover:bg-amber-50' },
  green: { active: 'bg-green-600 text-white', inactive: 'text-green-600 hover:bg-green-50' },
  red: { active: 'bg-red-500 text-white', inactive: 'text-red-600 hover:bg-red-50' },
  purple: { active: 'bg-purple-600 text-white', inactive: 'text-purple-600 hover:bg-purple-50' }
};

export const ResourceTopic = ({ subject = 'maths' }) => {
  const navigate = useNavigate();
  const { categoryId, topicId } = useParams();
  const [topicData, setTopicData] = useState(null);
  const [userProgress, setUserProgress] = useState({ completed_sections: [], quiz_scores: [] });
  const [loading, setLoading] = useState(true);
  // Default to 'concept' section
  const [activeSection, setActiveSection] = useState('concept');
  const [quizState, setQuizState] = useState({ currentQuestion: 0, answers: [], showResults: false });
  const [showHint, setShowHint] = useState(false);
  const [showSolution, setShowSolution] = useState(false);

  useEffect(() => {
    fetchTopicData();
  }, [categoryId, topicId, subject]);

  const fetchTopicData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [topicRes, progressRes] = await Promise.all([
        axios.get(`${API}/api/resources/${subject}/topic/${categoryId}/${topicId}`),
        token ? axios.get(`${API}/api/resources/progress/topic/${categoryId}/${topicId}`, { headers }).catch(() => null) : null
      ]);

      setTopicData(topicRes.data);
      if (progressRes?.data?.progress) {
        setUserProgress(progressRes.data.progress);
      }
    } catch (error) {
      console.error('Error fetching topic:', error);
      toast.error('Failed to load topic');
    } finally {
      setLoading(false);
    }
  };

  const markSectionComplete = async (section) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await axios.post(
        `${API}/api/resources/progress/mark-complete?category_id=${categoryId}&topic_id=${topicId}&section=${section}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserProgress(prev => ({
        ...prev,
        completed_sections: [...new Set([...(prev.completed_sections || []), section])]
      }));
      toast.success('Section marked as complete!');
    } catch (error) {
      console.error('Error marking complete:', error);
    }
  };

  const submitQuizScore = async (score) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await axios.post(
        `${API}/api/resources/progress/quiz-score?category_id=${categoryId}&topic_id=${topicId}&score=${score}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Quiz completed! Score: ${score}/5`);
    } catch (error) {
      console.error('Error saving score:', error);
    }
  };

  const handleQuizAnswer = (questionIndex, answerIndex) => {
    const newAnswers = [...quizState.answers];
    newAnswers[questionIndex] = answerIndex;
    setQuizState(prev => ({ ...prev, answers: newAnswers }));
  };

  const submitQuiz = () => {
    const questions = topicData.topic.quick_practice.questions;
    const score = quizState.answers.reduce((acc, answer, idx) => {
      return acc + (answer === questions[idx].correct ? 1 : 0);
    }, 0);
    setQuizState(prev => ({ ...prev, showResults: true }));
    submitQuizScore(score);
    markSectionComplete('practice');
  };

  const isSectionComplete = (sectionId) => {
    return userProgress.completed_sections?.includes(sectionId);
  };

  const handleTabChange = (sectionId) => {
    setActiveSection(sectionId);
    if (sectionId === 'practice') {
      setQuizState({ currentQuestion: 0, answers: [], showResults: false });
    }
    setShowHint(false);
    setShowSolution(false);
  };

  const completionPercent = Math.round(((userProgress.completed_sections?.length || 0) / 5) * 100);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <div className="text-slate-900">Loading...</div>
      </div>
    );
  }

  if (!topicData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <div className="text-slate-900">Topic not found</div>
      </div>
    );
  }

  const topic = topicData.topic;

  return (
    <div 
      data-testid="resource-topic-page"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white"
    >
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-slate-900 hover:bg-slate-100 flex-shrink-0"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-violet-600 mb-0.5">{topicData.category.name}</p>
              <h1 className="text-base font-bold text-slate-900 truncate">{topic.title}</h1>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Progress value={completionPercent} className="w-16 h-2 bg-slate-50" />
              <span className="text-xs text-slate-500">{completionPercent}%</span>
            </div>
          </div>
        </div>

        {/* Section Tabs - Sticky below header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-2">
            <div className="flex overflow-x-auto scrollbar-hide -mx-2 px-2 py-2 gap-1" style={{ WebkitOverflowScrolling: 'touch' }}>
              {getVisibleSections(topic, subject).map((section) => {
                const isActive = activeSection === section.id;
                const isComplete = isSectionComplete(section.id);
                const IconComponent = section.icon;
                const colors = TAB_COLORS[section.color];

                return (
                  <button
                    key={section.id}
                    onClick={() => handleTabChange(section.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                      isActive ? colors.active : colors.inactive
                    } ${isActive ? 'shadow-md' : ''}`}
                    data-testid={`tab-${section.id}`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span className="hidden sm:inline">{section.title}</span>
                    <span className="sm:hidden">{section.title.split(' ')[0]}</span>
                    {isComplete && !isActive && (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {activeSection === 'concept' && (
            /* Concept Explained Section */
            <motion.div
              key="concept"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="bg-white border-gray-200 shadow-lg">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl md:text-2xl font-bold text-gray-900">{topic.concept_explained.title}</h2>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {topic.concept_explained.reading_time}
                      </p>
                    </div>
                    <TTSPlayer
                      subject={subject}
                      categoryId={categoryId}
                      topicId={topicId}
                      section="concept"
                      label="Listen"
                    />
                  </div>
                  <div className="prose max-w-none">
                    <div className="text-gray-800 whitespace-pre-wrap leading-relaxed text-base font-normal" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>
                      {topic.concept_explained.content}
                    </div>
                  </div>
                  {!isSectionComplete('concept') && (
                    <Button
                      className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => markSectionComplete('concept')}
                      data-testid="complete-concept-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Mark as Complete
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeSection === 'example' && (
            /* Worked Example Section */
            <motion.div
              key="example"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="bg-white border-gray-200 shadow-lg">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                    <div className="p-2 rounded-lg bg-amber-100">
                      <Lightbulb className="w-5 h-5 text-amber-600" />
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex-1 min-w-0">{topic.worked_example.title}</h2>
                    <TTSPlayer
                      subject={subject}
                      categoryId={categoryId}
                      topicId={topicId}
                      section="example"
                      label="Listen"
                    />
                  </div>
                  
                  <div className="mb-6 p-5 rounded-lg bg-gray-50 border border-gray-200">
                    <p className="text-sm font-semibold text-slate-500 mb-2 uppercase tracking-wide">Problem:</p>
                    <p className="text-gray-900 font-medium text-lg" style={{ fontFamily: 'Georgia, serif' }}>{topic.worked_example.problem}</p>
                  </div>

                  <div className="p-5 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-sm font-semibold text-amber-700 mb-3 uppercase tracking-wide">Solution:</p>
                    <div className="text-gray-800 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>
                      {topic.worked_example.solution}
                    </div>
                  </div>

                  {!isSectionComplete('example') && (
                    <Button
                      className="mt-8 w-full bg-amber-500 hover:bg-amber-600 text-white"
                      onClick={() => markSectionComplete('example')}
                      data-testid="complete-example-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Mark as Complete
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeSection === 'practice' && (
            /* Quick Practice Section */
            <motion.div
              key="practice"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="bg-white border-gray-200 shadow-lg">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-100">
                        <Target className="w-5 h-5 text-green-600" />
                      </div>
                      <h2 className="text-xl md:text-2xl font-bold text-gray-900">{topic.quick_practice.title}</h2>
                    </div>
                    {!quizState.showResults && (
                      <Badge className="bg-gray-100 text-gray-700 border border-gray-300">
                        {quizState.currentQuestion + 1} / {topic.quick_practice.questions.length}
                      </Badge>
                    )}
                  </div>

                  {!quizState.showResults ? (
                    <div>
                      {topic.quick_practice.questions.map((q, qIdx) => (
                        <div key={q.id} className={qIdx === quizState.currentQuestion ? 'block' : 'hidden'}>
                          <p className="text-lg text-gray-900 mb-5 font-medium" style={{ fontFamily: 'Georgia, serif' }}>{q.question}</p>
                          <div className="space-y-3">
                            {q.options.map((option, oIdx) => (
                              <Button
                                key={oIdx}
                                variant="outline"
                                className={`w-full justify-start text-left h-auto py-4 px-5 ${
                                  quizState.answers[qIdx] === oIdx 
                                    ? 'bg-green-50 border-green-500 text-gray-900' 
                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                                onClick={() => handleQuizAnswer(qIdx, oIdx)}
                                data-testid={`option-${qIdx}-${oIdx}`}
                              >
                                <span className="mr-3 text-green-600 font-bold">{String.fromCharCode(65 + oIdx)}</span>
                                {option}
                              </Button>
                            ))}
                          </div>
                          <div className="flex gap-3 mt-6">
                            {qIdx > 0 && (
                              <Button
                                variant="outline"
                                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                                onClick={() => setQuizState(prev => ({ ...prev, currentQuestion: prev.currentQuestion - 1 }))}
                              >
                                Previous
                              </Button>
                            )}
                            {qIdx < topic.quick_practice.questions.length - 1 ? (
                              <Button
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                                disabled={quizState.answers[qIdx] === undefined}
                                onClick={() => setQuizState(prev => ({ ...prev, currentQuestion: prev.currentQuestion + 1 }))}
                                data-testid="next-question-btn"
                              >
                                Next Question
                              </Button>
                            ) : (
                              <Button
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                                disabled={quizState.answers.length < topic.quick_practice.questions.length}
                                onClick={submitQuiz}
                                data-testid="submit-quiz-btn"
                              >
                                Submit Quiz
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Quiz Results */
                    <div>
                      <div className="text-center mb-6">
                        <div className="inline-flex p-4 rounded-full bg-green-100 mb-3">
                          <Award className="w-10 h-10 text-green-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">Quiz Complete!</h3>
                        <p className="text-slate-500">
                          You scored {quizState.answers.reduce((acc, answer, idx) => {
                            return acc + (answer === topic.quick_practice.questions[idx].correct ? 1 : 0);
                          }, 0)} out of {topic.quick_practice.questions.length}
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        {topic.quick_practice.questions.map((q, qIdx) => {
                          const isCorrect = quizState.answers[qIdx] === q.correct;
                          return (
                            <div key={q.id} className={`p-5 rounded-lg border ${isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                              <p className="text-gray-900 mb-2 font-medium">{qIdx + 1}. {q.question}</p>
                              <p className={`text-sm font-semibold ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                                {isCorrect ? '✓ Correct!' : `✗ Incorrect. Correct answer: ${q.options[q.correct]}`}
                              </p>
                              <p className="text-sm text-slate-500 mt-2">{q.explanation}</p>
                            </div>
                          );
                        })}
                      </div>

                      <Button
                        className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => setQuizState({ currentQuestion: 0, answers: [], showResults: false })}
                        data-testid="retry-quiz-btn"
                      >
                        Try Again
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeSection === 'mistakes' && (
            /* Common Mistakes Section */
            <motion.div
              key="mistakes"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="bg-white border-gray-200 shadow-lg">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                    <div className="p-2 rounded-lg bg-red-100">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900">{topic.common_mistakes.title}</h2>
                  </div>

                  <div className="space-y-5">
                    {topic.common_mistakes.mistakes.map((mistake, idx) => (
                      <div key={idx} className="p-5 rounded-lg bg-red-50 border border-red-200">
                        <h4 className="font-semibold text-red-700 mb-2 text-lg">{mistake.mistake}</h4>
                        <p className="text-gray-700 mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                          <span className="font-medium">Example:</span> {mistake.example}
                        </p>
                        <div className="p-4 rounded bg-green-50 border border-green-200">
                          <p className="text-green-800" style={{ fontFamily: 'Georgia, serif' }}>
                            <span className="font-semibold">Tip:</span> {mistake.tip}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {!isSectionComplete('mistakes') && (
                    <Button
                      className="mt-8 w-full bg-red-500 hover:bg-red-600 text-white"
                      onClick={() => markSectionComplete('mistakes')}
                      data-testid="complete-mistakes-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Mark as Complete
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeSection === 'challenge' && (
            /* 11+ Twist Section */
            <motion.div
              key="challenge"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="bg-white border-gray-200 shadow-lg">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
                    <div className="p-2 rounded-lg bg-purple-100">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-gray-900">{topic.eleven_plus_twist.title}</h2>
                      <Badge className="mt-1 bg-purple-100 text-purple-700 border-purple-200">Challenge</Badge>
                    </div>
                  </div>

                  <div className="p-5 rounded-lg bg-gray-50 border border-gray-200 mb-6">
                    <p className="text-gray-900 whitespace-pre-wrap text-lg" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>{topic.eleven_plus_twist.question}</p>
                  </div>

                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={() => setShowHint(!showHint)}
                      data-testid="show-hint-btn"
                    >
                      <HelpCircle className="w-4 h-4 mr-2" />
                      {showHint ? 'Hide Hint' : 'Show Hint'}
                    </Button>
                    
                    {showHint && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="p-5 rounded-lg bg-amber-50 border border-amber-200"
                      >
                        <p className="text-amber-800" style={{ fontFamily: 'Georgia, serif' }}>{topic.eleven_plus_twist.hint}</p>
                      </motion.div>
                    )}

                    <Button
                      variant="outline"
                      className="w-full border-green-300 text-green-700 hover:bg-green-50"
                      onClick={() => setShowSolution(!showSolution)}
                      data-testid="show-solution-btn"
                    >
                      <Brain className="w-4 h-4 mr-2" />
                      {showSolution ? 'Hide Solution' : 'Show Solution'}
                    </Button>

                    {showSolution && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="p-5 rounded-lg bg-green-50 border border-green-200"
                      >
                        <div className="text-gray-800 whitespace-pre-wrap" style={{ fontFamily: 'Georgia, serif', lineHeight: '1.8' }}>{topic.eleven_plus_twist.solution}</div>
                      </motion.div>
                    )}
                  </div>

                  {!isSectionComplete('challenge') && (
                    <Button
                      className="mt-8 w-full bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => markSectionComplete('challenge')}
                      data-testid="complete-challenge-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Mark as Complete
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Floating Ask AI Tutor — available across every section so learners
          can ask follow-up questions without leaving the topic. */}
      <AskAITutorButton
        endpoint="/api/ai-tutor/chat"
        context={{
          subject,
          topic: topic.title,
          category: topicData.category?.name,
          section: activeSection,
        }}
      />
    </div>
  );
};

export default ResourceTopic;
